/**
 * Mesa integrada ao ERP da loja (`settingsWeb.mesaErpEnabled`).
 *
 * Em vez de só gravar o pedido na nuvem, o cardápio abre a mesa e lança os
 * itens direto no PDV da loja, via Laravel → RabbitMQ → Berp.MesaService
 * (contrato em `TotemApiv2/docs/MESA_SERVICE.md`).
 *
 * Fluxo, na hora de enviar o pedido:
 *  1. consulta o status da mesa (`mesa.status`) com o `clientOrderId` deste aparelho;
 *  2. mesa fechada → cliente digita o PIN e a mesa é aberta (`mesa.abrir`);
 *  3. mesa já aberta (garçom/PDV/outro aparelho) → entra na MESMA comanda, sem PIN
 *     (`api/mesa/entrar`): usa o `clientOrderId` que a comanda já tem;
 *  4. mesa em pagamento/conferência/encerrada → não lança: avisa e oferece chamar o garçom;
 *  5. lança (`mesa.lancar_itens`). Os próximos pedidos ("Pedir mais itens")
 *     reaproveitam a sessão e vão direto ao passo 5.
 */

import type { CartItem, Product } from '../types'

const round2 = (n: number) => Math.round(n * 100) / 100

export type MesaGate =
  /** Mesa fechada, ou com a venda já paga/cancelada: precisa do PIN para abrir uma nova. */
  | 'livre'
  /** Mesa aberta e vinculada a este aparelho: pode lançar. */
  | 'minha'
  /** Mesa aberta por outro canal (garçom/PDV/outro aparelho): entra na mesma comanda, sem PIN. */
  | 'ocupada'
  /** Mesa aberta, mas sem aceitar itens (pagamento/conferência/encerrada): chamar o garçom. */
  | 'bloqueada'

/** Resposta de `mesa.status` (campo `data`). */
export interface MesaStatusData {
  encontrada?: boolean
  movimentoAberto?: boolean
  clientOrderId?: string | null
  venStatus?: number | null
  status?: string
  podeLancarItens?: boolean
}

export function classifyMesa(data: MesaStatusData | null | undefined, clientOrderId: string): MesaGate {
  if (!data || data.encontrada === false || data.status === 'NAO_ENCONTRADA') return 'livre'

  // Venda finalizada (paga = 0, cancelada = 9): a mesa está livre para o próximo cliente,
  // que abre outra venda com o PIN. Só pagamento (3) e conferência (70) seguem bloqueados.
  if (data.venStatus === 0 || data.venStatus === 9 || data.status === 'PAGA' || data.status === 'CANCELADA') return 'livre'

  // Fechando conta/encerrada: ninguém lança, seja a comanda de quem for
  if (data.podeLancarItens === false || (data.venStatus != null && data.venStatus !== 1)) return 'bloqueada'

  const mine = !!data.clientOrderId && data.clientOrderId.toLowerCase() === clientOrderId.toLowerCase()
  return mine ? 'minha' : 'ocupada'
}

// ─── Sessão da mesa (aparelho) ───────────────────────────────────────────────

export interface MesaSession {
  clientOrderId: string
  mesaToken: string
  numeroMesa: number
  /** epoch ms */
  at: number
}

/** O token do Laravel vale 6h; renova-se antes para não falhar no meio do pedido. */
export const SESSION_MAX_AGE_MS = (6 * 60 - 10) * 60 * 1000

const sessionKey = (branchId: string, table: string) => `mesa_erp_${branchId}_${table}`
const idKey = (branchId: string, table: string) => `mesa_erp_id_${branchId}_${table}`

export function loadMesaSession(branchId: string, table: string, now = Date.now()): MesaSession | null {
  try {
    const raw = localStorage.getItem(sessionKey(branchId, table))
    if (!raw) return null
    const s = JSON.parse(raw) as Partial<MesaSession>
    if (!s.clientOrderId || !s.mesaToken || typeof s.at !== 'number') return null
    if (now - s.at >= SESSION_MAX_AGE_MS || now < s.at) return null
    return s as MesaSession
  } catch {
    return null
  }
}

export function saveMesaSession(branchId: string, table: string, s: Omit<MesaSession, 'at'>, now = Date.now()): void {
  try { localStorage.setItem(sessionKey(branchId, table), JSON.stringify({ ...s, at: now })) } catch { /* storage bloqueado */ }
}

export function clearMesaSession(branchId: string, table: string): void {
  try { localStorage.removeItem(sessionKey(branchId, table)) } catch { /* storage bloqueado */ }
}

/**
 * `clientOrderId` estável deste aparelho nesta mesa. Existe antes da sessão
 * porque é ele que o Laravel/loja usam para dizer "esta mesa é minha".
 */
export function getMesaClientOrderId(branchId: string, table: string): string {
  const existing = loadMesaSession(branchId, table)?.clientOrderId
  if (existing) return existing
  try {
    const stored = localStorage.getItem(idKey(branchId, table))
    if (stored) return stored
    const fresh = crypto.randomUUID()
    localStorage.setItem(idKey(branchId, table), fresh)
    return fresh
  } catch {
    return crypto.randomUUID()
  }
}

/** A venda deste id terminou (paga/cancelada): o próximo pedido nesta mesa abre uma venda nova, com id novo. */
export function resetMesaClientOrderId(branchId: string, table: string): void {
  clearMesaSession(branchId, table)
  try { localStorage.removeItem(idKey(branchId, table)) } catch { /* storage bloqueado */ }
}

/** Venda encerrada: não se lança mais nela, mas a mesa pode ser aberta de novo. */
export const MESA_ENDED_CODES = ['MESA_JA_PAGA', 'MESA_CANCELADA']

/** A mesa foi aberta com outro `clientOrderId` (resposta de `abrir` reaproveitou uma sessão): passa a valer o dele. */
export function adoptClientOrderId(branchId: string, table: string, clientOrderId: string): void {
  try { localStorage.setItem(idKey(branchId, table), clientOrderId) } catch { /* storage bloqueado */ }
}

// ─── Itens do pedido ─────────────────────────────────────────────────────────

export interface MesaItemInput {
  /** _id do cadastro: o Laravel resolve daqui o código do PDV e o preço (o mesmo código do ERP se repete em vários cadastros). */
  produtoId: string
  codigoProduto: number
  quantidade: number
  /** Ignorado pelo Laravel (vale o catálogo); enviado só como referência. */
  valorUnitario: number
  observacao: string | null
  complements?: Array<{
    produtoId: string
    codigoProduto: number
    quantidade: number
    valorUnitario: number
    /** Fração do complemento (meia pizza = 0,5). Ausente = inteiro. */
    fracao?: number
    observacao: string | null
  }>
}

/** Código do ERP é numérico; produto sem código não pode ir para a loja. */
export function erpCode(code: string | undefined): number | null {
  const n = Number(String(code ?? '').trim())
  return Number.isInteger(n) && n > 0 ? n : null
}

export class MesaItemError extends Error {
  readonly productName: string

  constructor(productName: string) {
    super(`${productName} ainda não tem código no sistema da loja. Peça ao garçom.`)
    this.name = 'MesaItemError'
    this.productName = productName
  }
}

/**
 * Converte o carrinho no formato de `mesa.lancar_itens`. Complemento da mesma
 * escolha vai aninhado no item (a loja encadeia no produto pai). Embalagem fica
 * de fora: consumo no local não leva embalagem.
 *
 * @throws MesaItemError se algum produto/complemento não tiver código do ERP
 */
export function buildMesaItems(items: CartItem[], products: Product[]): MesaItemInput[] {
  const byId = new Map(products.map((p) => [p._id, p]))

  return items.map((item) => {
    const codigoProduto = erpCode(item.product.code)
    if (codigoProduto === null) throw new MesaItemError(item.product.name)

    const complements = item.addedComplements
      .filter((c) => !c.isPackaging)
      .map((c) => {
        const code = erpCode(byId.get(c._id)?.code)
        if (code === null) throw new MesaItemError(c.name)
        const fracao = c.unitFraction !== undefined && c.unitFraction > 0 && c.unitFraction < 1 ? c.unitFraction : undefined
        return {
          produtoId: c._id,
          codigoProduto: code,
          quantidade: c.quantity,
          valorUnitario: round2(c.price * (c.unitFraction ?? 1)),
          ...(fracao ? { fracao } : {}),
          observacao: null,
        }
      })

    return {
      produtoId: item.product._id,
      codigoProduto,
      quantidade: item.quantity,
      valorUnitario: item.product.price,
      observacao: item.note.trim() || null,
      ...(complements.length ? { complements } : {}),
    }
  })
}

// ─── Mensagens ───────────────────────────────────────────────────────────────

/** Texto para o cliente, a partir do código de erro devolvido pelo Laravel/loja. */
export function mesaErrorMessage(code: string | undefined, fallback?: string): string {
  switch (code) {
    case 'PIN_INVALIDO': return 'PIN incorreto. Confira o número da mesa e a hora.'
    case 'PIN_BLOQUEADO': return 'Muitas tentativas incorretas. Aguarde 2 minutos e tente de novo.'
    case 'MOVIMENTO_FECHADO': return 'O caixa do restaurante está fechado no momento. Solicite atendimento ao garçom.'
    case 'MESA_OCUPADA': return 'Esta mesa já está em uso. Chame o garçom.'
    case 'MESA_FECHADA': return 'A mesa ainda não está aberta. Digite o PIN para abrir.'
    case 'MESA_BLOQUEADA_PAGAMENTO': return 'Conta em processo de pagamento. Não é possível pedir agora.'
    case 'MESA_EM_CONFERENCIA': return 'Pré-conta solicitada no salão. Não é possível pedir agora.'
    case 'MESA_JA_PAGA': return 'Esta conta já foi paga. Leia o QR Code da mesa novamente.'
    case 'MESA_CANCELADA': return 'Esta conta foi cancelada. Chame o garçom.'
    case 'ITEM_INDISPONIVEL': return 'Algum item não está disponível. Remova-o do carrinho e tente de novo.'
    case 'LOJA_OFFLINE_TIMEOUT': return 'A cozinha não respondeu. O pedido NÃO foi lançado. Toque em "Tentar novamente" ou chame o garçom.'
    case 'TOKEN_MESA_INVALIDO':
    case 'TOKEN_MESA_DIVERGENTE': return 'Sua sessão da mesa expirou. Confirme o pedido de novo.'
    default: return fallback ?? 'Não foi possível enviar o pedido para a cozinha. Chame o garçom.'
  }
}

/** Erros em que reabrir a sessão (novo status/PIN) resolve. */
export const SESSION_ERROR_CODES = ['TOKEN_MESA_INVALIDO', 'TOKEN_MESA_DIVERGENTE']

// ─── Conta da mesa (mesa.conta) ──────────────────────────────────────────────

export interface MesaBillItem {
  id: number | string
  codigoProduto: number | null
  descricao: string
  categoria: string
  quantidade: number
  valorUnitario: number
  valorTotal: number
  observacao: string | null
  horaLancamento: string | null
  cancelado: boolean
  nomeCliente: string | null
  quemLancou: string | null
  origem: string | null
  /** Sequência do item na venda e do item pai (0 = item principal). */
  seqPro: number
  seqProPai: number
  /** Itens de dentro do combo / adicionais / sabores, já em árvore. */
  filhos: MesaBillItem[]
}

export interface MesaBillPayment {
  id: number | string
  forma: string
  valor: number
  dataHora: string | null
}

export interface MesaBill {
  /** true = a loja não respondeu: valores do último espelho guardado na nuvem. */
  offline: boolean
  notice: string | null
  status: string
  garcomNome: string | null
  horaAbertura: string | null
  horaUltimoLancamento: string | null
  totais: { produtos: number; servico: number; desconto: number; bruto: number; pago: number; restante: number }
  itens: MesaBillItem[]
  pagamentos: MesaBillPayment[]
}

const num = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null)
type Rec = Record<string, unknown>
const rec = (v: unknown): Rec => (v && typeof v === 'object' ? (v as Rec) : {})

/**
 * Item cancelado: `cancelado` verdadeiro (true, 1, "1", "S") ou, se o serviço mandar
 * as colunas do ERP, `VIT_CANCEL = 1` com `VIT_DHCANCEL` preenchido.
 */
export function isBillItemCancelled(i: Rec): boolean {
  const truthy = (v: unknown) => v === true || v === 1 || (typeof v === 'string' && ['1', 's', 'true'].includes(v.trim().toLowerCase()))
  if (truthy(i['cancelado'])) return true
  const flag = i['VIT_CANCEL'] ?? i['vitCancel']
  const when = i['VIT_DHCANCEL'] ?? i['vitDhcancel'] ?? i['dhCancelamento']
  return truthy(flag) && when != null && String(when).trim() !== ''
}

function parseBillItem(i: Rec, idx: number): MesaBillItem {
  return {
    id: (i['id'] as number | string | undefined) ?? idx,
    codigoProduto: erpCode(String(i['codigoProduto'] ?? '')),
    descricao: str(i['descricao']) ?? str(i['name']) ?? 'Item',
    categoria: str(i['categoria']) ?? 'Outros',
    quantidade: num(i['quantidade'] ?? i['quantity']),
    valorUnitario: num(i['valorUnitario'] ?? i['price']),
    valorTotal: num(i['valorTotal'] ?? i['total']),
    observacao: str(i['observacao']) ?? str(i['note']),
    horaLancamento: str(i['horaLancamento']),
    cancelado: isBillItemCancelled(i),
    nomeCliente: str(i['nomeCliente']),
    quemLancou: str(i['quemLancou']),
    origem: str(i['origem']),
    seqPro: num(i['seqPro']),
    seqProPai: num(i['seqProPai']),
    filhos: [],
  }
}

/**
 * Monta a árvore da conta. A loja manda cada item principal com seus
 * complementos aninhados em `complements`; se vier uma lista plana (só com
 * `seqProPai`), o filho é pendurado no pai pela sequência.
 */
export function buildBillTree(raw: Rec[]): MesaBillItem[] {
  const roots = raw.map((r, idx) => {
    const item = parseBillItem(r, idx)
    const nested = Array.isArray(r['complements']) ? r['complements'] as Rec[]
      : Array.isArray(r['complementos']) ? r['complementos'] as Rec[] : []
    item.filhos = buildBillTree(nested)
    return item
  })

  // Formato plano: filho solto na lista, apontando para o pai por seqProPai
  const bySeq = new Map(roots.filter((r) => r.seqPro > 0).map((r) => [r.seqPro, r]))
  const top: MesaBillItem[] = []
  for (const r of roots) {
    const parent = r.seqProPai > 0 ? bySeq.get(r.seqProPai) : undefined
    if (parent && parent !== r) parent.filhos.push(r)
    else top.push(r)
  }
  return top
}

/** Mesmo produto, mesma pessoa, mesma observação, mesmo preço e mesmos complementos. */
function groupKey(i: MesaBillItem): string {
  const kids = i.filhos.map((k) => [k.codigoProduto, k.descricao, k.quantidade, k.valorUnitario, k.observacao, k.cancelado].join('~'))
  return [
    i.codigoProduto, i.descricao, i.valorUnitario, i.observacao, i.cancelado,
    i.nomeCliente, i.quemLancou, i.origem, ...kids,
  ].join('|')
}

/**
 * Junta lançamentos idênticos ("1× Delícia" quatro vezes vira "4× Delícia").
 * Cancelado nunca se mistura com ativo e a ordem da primeira aparição é mantida.
 * Os complementos, que são iguais por construção, têm quantidade e valor somados;
 * o horário vira a faixa do primeiro ao último lançamento.
 */
export function groupIdenticalItems(itens: MesaBillItem[]): MesaBillItem[] {
  const groups = new Map<string, MesaBillItem[]>()
  for (const i of itens) {
    const key = groupKey(i)
    const list = groups.get(key)
    if (list) list.push(i)
    else groups.set(key, [i])
  }
  return [...groups.values()].map((list) => {
    if (list.length === 1) return list[0]
    const first = list[0]
    const sorted = list.map((x) => x.horaLancamento).filter((h): h is string => !!h).sort()
    return {
      ...first,
      quantidade: list.reduce((a, x) => a + x.quantidade, 0),
      valorTotal: list.reduce((a, x) => a + x.valorTotal, 0),
      horaLancamento: sorted.length === 0 ? null
        : sorted[0] === sorted[sorted.length - 1] ? sorted[0] : `${sorted[0]} a ${sorted[sorted.length - 1]}`,
      filhos: first.filhos.map((k, idx) => ({
        ...k,
        quantidade: list.reduce((a, x) => a + x.filhos[idx].quantidade, 0),
        valorTotal: list.reduce((a, x) => a + x.filhos[idx].valorTotal, 0),
      })),
    }
  })
}

/**
 * Valor dos itens cancelados (o que saiu da conta). O item cancelado leva o que está
 * dentro dele; filho cancelado de pai ativo conta sozinho; nada é somado duas vezes.
 */
export function cancelledTotal(itens: MesaBillItem[], parentCancelled = false): number {
  const sumAll = (i: MesaBillItem): number => i.valorTotal + i.filhos.reduce((a, k) => a + sumAll(k), 0)
  return itens.reduce((acc, i) => {
    if (parentCancelled) return acc
    if (i.cancelado) return acc + sumAll(i)
    return acc + cancelledTotal(i.filhos)
  }, 0)
}

/** Todos os itens, pais e filhos, em ordem. Filho de item cancelado também conta como cancelado. */
export function flattenBill(itens: MesaBillItem[], parentCancelled = false): MesaBillItem[] {
  return itens.flatMap((i) => {
    const cancelado = parentCancelled || i.cancelado
    return [{ ...i, cancelado }, ...flattenBill(i.filhos, cancelado)]
  })
}

/** Valor do item com tudo que está dentro dele (o complemento é cobrado além do pai). Cancelado = 0. */
export function billItemTotal(i: MesaBillItem): number {
  if (i.cancelado) return 0
  return Math.round((i.valorTotal + i.filhos.reduce((acc, f) => acc + billItemTotal(f), 0)) * 100) / 100
}

/** Normaliza a resposta de `POST api/mesa/conta` (online da loja, ou espelho offline da nuvem). */
export function parseMesaBill(res: Rec): MesaBill {
  const offline = res['origem'] === 'CACHE_OFFLINE_MONGODB'
  const d = rec(offline ? res['dados'] : res['data'])
  const t = rec(d['totais'])
  const garcom = rec(d['garcom'])

  const totais = offline
    ? {
      produtos: num(t['subtotal']), servico: num(t['taxaServico']), desconto: num(t['desconto']),
      bruto: num(t['total']), pago: num(t['totalPago']), restante: num(t['saldoPendente']),
    }
    : {
      produtos: num(t['valorProdutos']), servico: num(t['valorServico']), desconto: num(t['valorDesconto']),
      bruto: num(t['valorBruto']), pago: num(t['valorTotalPago']), restante: num(t['saldoRestante']),
    }

  const itens = buildBillTree(Array.isArray(d['itens']) ? d['itens'] as Rec[] : [])

  const pagamentos = (Array.isArray(d['pagamentos']) ? d['pagamentos'] as Rec[] : []).map((p, idx): MesaBillPayment => ({
    id: (p['id'] as number | string | undefined) ?? idx,
    forma: str(p['formaPagamentoNome']) ?? 'Pagamento',
    valor: num(p['valor']),
    dataHora: str(p['dataHora']),
  }))

  return {
    offline,
    notice: offline ? (str(res['aviso']) ?? 'Exibindo a última sincronização da mesa.') : null,
    status: str(d['statusDescricao']) ?? str(d['statusMesa']) ?? 'ABERTA',
    garcomNome: str(garcom['nome']),
    horaAbertura: str(d['horaAbertura']),
    horaUltimoLancamento: str(d['horaUltimoLancamento']),
    totais,
    itens,
    pagamentos,
  }
}

export const BILL_STATUS_LABEL: Record<string, string> = {
  ABERTA: 'Mesa aberta',
  AGUARDANDO_PAGAMENTO: 'Aguardando pagamento',
  EM_CONFERENCIA: 'Pré-conta solicitada',
  PAGA: 'Conta paga',
  CANCELADA: 'Conta cancelada',
}

/**
 * Item lançado pelo atendimento (garçom/balcão/PDV) e não por um cliente do cardápio.
 * Canal `PDV`, ou item sem nome de cliente que não veio do cardápio.
 */
export function isStaffItem(i: MesaBillItem): boolean {
  const canal = (i.origem ?? '').toUpperCase()
  return canal === 'PDV' || (i.nomeCliente === null && canal !== 'CARDAPIO_DIGITAL')
}

/**
 * Quem lançou o item, como a loja informou (`quemLancou`): o nome do garçom nos
 * itens do PDV, o nome do cliente nos do cardápio. Só cai no rótulo genérico
 * quando a loja não diz quem foi.
 */
export function billPersonLabel(i: MesaBillItem): string {
  if (isStaffItem(i)) return i.quemLancou ?? STAFF_LABEL
  return i.nomeCliente ?? i.quemLancou ?? 'Cliente'
}

export interface BillGroup { key: string; label: string; count: number; total: number }

/**
 * Itens cancelados (o próprio item marcado, não os que só estão dentro de um pai cancelado).
 * Cancelado não entra no total, mas nunca pode sumir da tela: o cliente que vê a conta
 * diferente do que pediu precisa ver o que foi tirado.
 */
export function cancelledItems(itens: MesaBillItem[]): MesaBillItem[] {
  return itens.flatMap((i) => [...(i.cancelado ? [i] : []), ...cancelledItems(i.filhos)])
}

export interface GroupEntry {
  item: MesaBillItem
  /** Item principal, quando este é um item de dentro de um combo. */
  parent: MesaBillItem | null
}

/**
 * Os itens que formam um grupo de `groupBill` (a mesma regra: cancelado fora, itens de
 * dentro de combo contam como itens). A soma de `valorTotal` das entradas é o total do grupo.
 */
export function groupEntries(itens: MesaBillItem[], by: 'pessoa' | 'categoria', label: string): GroupEntry[] {
  const out: GroupEntry[] = []
  const walk = (list: MesaBillItem[], parent: MesaBillItem | null, parentCancelled: boolean) => {
    for (const i of list) {
      const cancelado = parentCancelled || i.cancelado
      if (!cancelado) {
        const key = by === 'pessoa' ? billPersonLabel(i) : i.categoria
        if (key === label) out.push({ item: i, parent })
      }
      walk(i.filhos, i, cancelado)
    }
  }
  walk(itens, null, false)
  return out
}

/** Soma por pessoa ou por categoria. Item cancelado não entra. Maior consumo primeiro. */
export function groupBill(itens: MesaBillItem[], by: 'pessoa' | 'categoria'): BillGroup[] {
  const map = new Map<string, BillGroup>()
  // Os itens de dentro de combos também contam (a bebida do combo é "Bebidas")
  for (const i of flattenBill(itens)) {
    if (i.cancelado) continue
    const label = by === 'pessoa' ? billPersonLabel(i) : i.categoria
    const g = map.get(label) ?? { key: label, label, count: 0, total: 0 }
    g.count += 1
    g.total = Math.round((g.total + i.valorTotal) * 100) / 100
    map.set(label, g)
  }
  return [...map.values()].sort((a, b) => b.total - a.total)
}

// ─── Cores por pessoa na conta ───────────────────────────────────────────────

export interface PillColor { bg: string; fg: string }

/** Pessoas do cardápio: cores distintas, legíveis no tema claro (o app é travado em claro). */
const PERSON_PALETTE: PillColor[] = [
  { bg: '#dbeafe', fg: '#1e40af' }, // azul
  { bg: '#fce7f3', fg: '#9d174d' }, // rosa
  { bg: '#dcfce7', fg: '#166534' }, // verde
  { bg: '#ede9fe', fg: '#5b21b6' }, // violeta
  { bg: '#ffedd5', fg: '#9a3412' }, // laranja
  { bg: '#cffafe', fg: '#155e75' }, // ciano
  { bg: '#fef9c3', fg: '#854d0e' }, // amarelo
]

/** Garçom/balcão: neutro, para nunca ser confundido com um cliente da mesa. */
export const STAFF_PILL: PillColor = { bg: '#e5e7eb', fg: '#374151' }

export const STAFF_LABEL = 'Garçom / Balcão'

/**
 * Uma cor por pessoa, na ordem em que aparecem na conta: duas pessoas da mesma
 * mesa nunca dividem cor (até 7; depois a paleta recomeça). Não é hash do nome
 * de propósito — hash colide, e a pílula só serve se distinguir quem pediu o quê.
 */
export function buildPersonColors(itens: MesaBillItem[]): Map<string, PillColor> {
  const colors = new Map<string, PillColor>([[STAFF_LABEL, STAFF_PILL]])
  let next = 0
  for (const i of itens) {
    const label = billPersonLabel(i)
    if (colors.has(label)) continue
    // Todo o atendimento usa o cinza (cada garçom com o seu nome); cor só para clientes
    if (isStaffItem(i)) { colors.set(label, STAFF_PILL); continue }
    colors.set(label, PERSON_PALETTE[next % PERSON_PALETTE.length])
    next += 1
  }
  return colors
}

/** Canal que lançou o item, em português. */
export function billOriginLabel(origem: string | null): string {
  switch ((origem ?? '').toUpperCase()) {
    case 'CARDAPIO_DIGITAL': return 'Cardápio digital'
    case 'PDV': return 'Garçom / PDV'
    default: return origem ?? '—'
  }
}
